import { createFileRoute } from "@tanstack/react-router"
import { usePageTracking } from "~/hooks/usePageTracking"
import PurchasePlanList from "~/components/purchase-plan/PurchasePlanList"
import { CreateFashionPurchasePlanDialog } from "~/components/fashion-supply-plan/CreateFashionPurchasePlanDialog"
import { FashionSupplyComparisonCard } from "~/components/fashion-supply-plan/FashionSupplyComparisonCard"
import { FashionSupplyCollectionBreakdownCard } from "~/components/fashion-supply-plan/FashionSupplyCollectionBreakdownCard"
import { IncomingOrdersProvider } from "~/context/IncomingOrdersProvider"
import { IncomingOrdersTableSection } from "~/components/purchaseOrder/incomingOrders/IncomingOrdersOverview"
import PurchaseOrdersTable from "~/components/purchaseOrder/tabs/PurchaseOrdersTable"
import { useCollectionDetailContext } from "~/context/CollectionDetailProvider"
import { OverviewPage } from "~/components/design-system/pages"

export const Route = createFileRoute(
  "/_auth/procurement/supply-plan/collection/$collectionId/_layout/overview",
)({
  component: RouteComponent,
})

function RouteComponent() {
  usePageTracking("Fashion Supply Plan Detail - Overview")

  const {
    filteredPurchasePlans,
    collectionsWithFreeWarehouses,
    collectionIdsArray,
    collectionBreakdownData,
    showCollectionBreakdown,
    totalProjectedDemand,
    totalIncomingOrderUnits,
    totalPurchaseOrderUnits,
    purchaseOrdersLoading,
    collectionDetailsLoading,
    incomingOrdersLoading,
    filteredVariantsForIncomingOrders,
    hasExternalCollectionId,
    hasShipmentMethod,
    hasMultipleWarehouses,
    hasVendorId,
  } = useCollectionDetailContext()

  return (
    <OverviewPage fillContent contentClassName="gap-4">
      <div className={`grid gap-4 ${showCollectionBreakdown ? "grid-cols-2" : "grid-cols-2"}`}>
        <FashionSupplyComparisonCard
          isLoading={purchaseOrdersLoading || collectionDetailsLoading}
          totalForecastedDemand={totalProjectedDemand ?? 0}
          totalIncomingUnits={totalIncomingOrderUnits + totalPurchaseOrderUnits}
        />
        {showCollectionBreakdown && (
          <FashionSupplyCollectionBreakdownCard
            collections={collectionBreakdownData}
            isLoading={purchaseOrdersLoading || collectionDetailsLoading}
          />
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-fg-base">Buys</h2>
          <CreateFashionPurchasePlanDialog availableCollections={collectionsWithFreeWarehouses} />
        </div>
        {filteredPurchasePlans.length === 0 ? (
          <div className="flex h-[500px] items-center justify-center rounded-lg border border-base bg-base p-4">
            <p className="text-fg-muted">No buys associated with this collection</p>
          </div>
        ) : (
          <div className="h-[500px]">
            <PurchasePlanList
              purchasePlans={filteredPurchasePlans}
              displayFashionColumns
              availableCollections={collectionsWithFreeWarehouses}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-medium text-fg-base">Purchase Orders</h2>
        <div className="h-[500px]">
          <PurchaseOrdersTable collectionIds={collectionIdsArray} className="p-0" />
        </div>
      </div>

      <div className="flex flex-col">
        <h2 className="text-lg font-medium text-fg-base">Incoming Orders</h2>
        <div className="h-[500px]">
          <IncomingOrdersProvider
            variants={filteredVariantsForIncomingOrders}
            hasShipmentMethod={hasShipmentMethod}
            hasMultipleWarehouses={hasMultipleWarehouses}
            hasExternalCollectionId={hasExternalCollectionId}
            hasVendorId={hasVendorId}
            isLoading={incomingOrdersLoading}
          >
            <IncomingOrdersTableSection />
          </IncomingOrdersProvider>
        </div>
      </div>
    </OverviewPage>
  )
}
